/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { animated, SpringConfig, useSpring } from '@react-spring/web';
import { easeQuadOut } from 'd3-ease';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import tinycolor from 'tinycolor2';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  HorseColorValues,
  selectGameId,
  selectHorse,
  selectHorseDropped,
  selectHorseField,
  selectHorseFinished,
  selectHorseHandi,
  selectHorseRanksCorrected,
  selectHorsesCount,
  selectIsGameRunning,
  selectIsHighlightedHorse,
  selectIsOwnHighlightedHorse,
  selectIsOwnHorseIndex,
  selectPlayer,
  selectRound,
  useSelector,
} from '~/app';
import HorseIcon from '~/components/HorseIcon';
import HorseInfo from '~/components/HorseIcon/HorseInfo';
import Turfmaster from '~/server/src/tmmodel';
import {
  boardXFromRelX,
  boardYFromRelY,
  ch,
  cw,
  transformRelX,
  transformRelY,
  vh,
  vw,
} from '~/utils';
import { cn } from '~/utils/styles';

import { getStepDurationMs } from './animationConfig';

/** Normalized (0–1) XY tolerance: avoids micro-snaps from float noise; ~sub-pixel at typical board scale. */
const RECONCILE_XY_EPS = 1e-4;
/** When path[0] in board space does not match rendered position, replace first point for live playback. */
const PATH_START_XY_EPS = 1e-4;

const supportsCssColorMix =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('color', 'color-mix(in srgb, white, black)');

/** srgb mix fallback for Safari < 16.2 (no color-mix). */
function mixSrgbHex(a: string, aWeight: number, b: string, bWeight: number): string {
  const total = aWeight + bWeight;
  const towardB = total > 0 ? (bWeight / total) * 100 : 0;
  return tinycolor.mix(a, b, towardB).toHexString();
}

/** Light horse-tinted anchor ring: slightly more color than orbit; kept a bit transparent so it stays soft. */
function activeHorseRingStroke(horseColorIndex: number): string {
  const hex = (HorseColorValues as Record<number, string>)[horseColorIndex];
  if (supportsCssColorMix) {
    const paleTint = hex ? `color-mix(in srgb, #ffffff 84%, ${hex} 16%)` : '#ffffff';
    return `color-mix(in srgb, transparent 18%, ${paleTint} 82%)`;
  }
  const paleTint = hex ? mixSrgbHex('#ffffff', 84, hex, 16) : '#ffffff';
  return tinycolor(paleTint).setAlpha(0.82).toRgbString();
}

/** Very light, fully opaque orbit arcs so they pop over the disc (whiter than the anchor ring). */
function activeHorseOrbitStroke(horseColorIndex: number): string {
  const hex = (HorseColorValues as Record<number, string>)[horseColorIndex];
  if (supportsCssColorMix) {
    return hex ? `color-mix(in srgb, #ffffff 99%, ${hex} 1%)` : '#ffffff';
  }
  return hex ? mixSrgbHex('#ffffff', 99, hex, 1) : '#ffffff';
}

/** Ring thickness (vw); box-shadow draws outside the disc so the inner edge sits flush on the horse circle. */
const ANCHOR_RING_WIDTH_VW_DEFAULT = '0.12vw';
const ANCHOR_RING_WIDTH_VW_OWN = '0.18vw';
/** viewBox 0–100: horse disc radius 50; stroke centered so its inner edge is at r=50. */
const ORBIT_STROKE_WIDTH_VIEWBOX = 5.25;
const ORBIT_RADIUS_VIEWBOX = 50 + ORBIT_STROKE_WIDTH_VIEWBOX / 2;
/** ~60° arc + long gap; two circles rotated 180° apart give parallel arcs on opposite sides. */
const ORBIT_DASH_LENGTH = 54;
const ORBIT_DASH_GAP = 258;

export interface HorseProps {
  /**
   * Horse id is basically the index of the horse data object in game state.
   */
  index: number;
}

const stableHorseFieldCache = new Map<
  string,
  { x: number; y: number; move?: { x: number; y: number }[] }
>();

function horseFieldCacheKey(gameId: unknown, horseIndex: number): string {
  const normalizedGameId =
    gameId == null || String(gameId).trim().length === 0 ? 'no-game' : String(gameId);
  return `${normalizedGameId}:${horseIndex}`;
}

const Horse = ({ index }: HorseProps) => {
  const analytics = useFirebaseAnalytics();
  const currentRound = useSelector(selectRound);
  const gameId = useSelector(selectGameId);
  const horse = useSelector((state) => selectHorse(state, index));
  const horseCount = useSelector(selectHorsesCount);
  const horseColor = horse?.color ?? 0;
  const horseRealColor = horse?.realColor ?? -1;
  const horseName = horse?.name ?? '';
  const isDropped = useSelector((state) => selectHorseDropped(state, index));
  const horseFinished = useSelector((state) => selectHorseFinished(state, index));
  const { rank, totalRank } = useSelector((state) => selectHorseRanksCorrected(state, index));
  const horseHandi = useSelector((state) => selectHorseHandi(state, index));
  const horsePlayerId = horse ? horse.playerId : -1;
  const horseIdentityKey = `${horseRealColor}:${horseName}:${horsePlayerId}`;
  const horsePlayer = useSelector((state) => selectPlayer(state, horsePlayerId));
  const isOwnHorse = useSelector((state) => selectIsOwnHorseIndex(state, index));

  const playerName = horsePlayer ? horsePlayer.name : '';
  const isGameRunning = useSelector(selectIsGameRunning);
  const cacheKey = useMemo(() => horseFieldCacheKey(gameId, index), [gameId, index]);

  const isHighlightedHorse = useSelector((state) => selectIsHighlightedHorse(state, index));
  const isOwnHighlightedHorse = useSelector((state) => selectIsOwnHighlightedHorse(state, index));

  const activeRingStroke = useMemo(() => activeHorseRingStroke(horseColor), [horseColor]);
  const activeOrbitStroke = useMemo(() => activeHorseOrbitStroke(horseColor), [horseColor]);

  const w = `2.5vw`;
  const h = `2.5vw`;

  // Pregame layout: distinct stable X positions so all horses never render stacked.
  // This is intentionally viewport-relative and tuned to the old pregame CSS layout.
  // It is only used as the spring's initial position; live game positioning always
  // comes from Redux/animationIntent.
  const preGameXRel = 0.5125 + 0.03 * (index - horseCount / 2);
  const preGameYRel = transformRelY(ch / 2);
  const centerSpreadXY = useMemo<[number, number]>(
    () => [preGameXRel, preGameYRel],
    [preGameXRel, preGameYRel],
  );

  const field = useSelector((state) => selectHorseField(state, index));
  if (field && Number.isFinite(field.x) && Number.isFinite(field.y)) {
    // Module-level cache bridges transient undefined snapshots without refs during render.
    stableHorseFieldCache.set(cacheKey, field);
  }
  const cachedField = stableHorseFieldCache.get(cacheKey);
  // Never use cached fields while in pre-game: they can leak previous-game positions.
  const fieldForDisplay = field ?? (isGameRunning ? cachedField : undefined);
  const hasUsableField =
    !!fieldForDisplay && Number.isFinite(fieldForDisplay.x) && Number.isFinite(fieldForDisplay.y);
  const hasBoardCoordinate =
    hasUsableField && (Math.abs(fieldForDisplay.x) > 1e-6 || Math.abs(fieldForDisplay.y) > 1e-6);
  const shouldUseField =
    hasUsableField && !isDropped && !horseFinished && (isGameRunning || hasBoardCoordinate);
  const { y, move, animationIntent } = shouldUseField
    ? fieldForDisplay
    : { y: ch / 2, move: [{ x: cw / 2, y: ch / 2 }], animationIntent: undefined };

  const path = useMemo(
    () => animationIntent?.path ?? move ?? [{ x: cw / 2, y: ch / 2 }],
    [animationIntent?.path, move],
  );
  const sequenceId = animationIntent?.sequenceId ?? -1;
  const speedProfile = animationIntent?.speedProfile ?? 'live';
  const mode = animationIntent?.mode;

  const lastProcessedSeqIdRef = useRef(-1);
  // Initial position from path (render-safe); ref kept for effect-time updates only
  const [initialPosition] = useState<[number, number]>(() => {
    if (!shouldUseField) {
      // While not running, we don't have reliable lane coordinates yet.
      // Use stable pregame scatter positions to avoid a "stack at center" flash.
      return centerSpreadXY;
    }
    const first = path[0];
    if (!first) return [transformRelX(cw / 2), transformRelY(ch / 2)];
    return [transformRelX(first.x), transformRelY(first.y)];
  });
  const lastPositionRef = useRef<[number, number]>(initialPosition);
  /**
   * Normalized XY matching what is actually shown (teleport uses field; spring uses `xy`).
   * Used as the handoff baseline when a new `sequenceId` starts so the first live move does not
   * originate from a stale spring value (e.g. center) after teleport/direct transform.
   */
  const lastRenderedXYRef = useRef<[number, number]>(initialPosition);
  const activeSequenceIdRef = useRef<number>(-1);
  /** While set, reconciliation must not snap spring to Redux end during multi-step path playback. */
  const pathPlaybackSeqRef = useRef<number | null>(null);
  const prevFinishedOrDroppedRef = useRef(false);
  const prevHorseIdentityKeyRef = useRef<string | null>(null);

  const [{ xy }, animatedPositionApi] = useSpring(
    () => ({
      from: { xy: initialPosition },
      config: { duration: 300, easing: easeQuadOut },
    }),
    [],
  );

  useEffect(() => {
    lastPositionRef.current = initialPosition;
  }, [initialPosition]);

  useEffect(() => {
    // Prune cache across game switches (module-level map survives between games/routes).
    // Keep only current game's entries to avoid leaks and unbounded growth.
    const prefix = `${horseFieldCacheKey(gameId, 0).split(':')[0]}:`;
    for (const k of stableHorseFieldCache.keys()) {
      if (!k.startsWith(prefix)) stableHorseFieldCache.delete(k);
    }
  }, [gameId]);

  useEffect(() => {
    if (!isGameRunning) {
      // Ensure pre-game never shows a cached coordinate from a previous game.
      stableHorseFieldCache.delete(cacheKey);
    }
  }, [cacheKey, isGameRunning]);

  useEffect(() => {
    if (field && Number.isFinite(field.x) && Number.isFinite(field.y)) {
      // Keep last valid field to survive transient undefined snapshots.
      stableHorseFieldCache.set(cacheKey, field);
    }
  }, [cacheKey, field]);

  useEffect(() => {
    if (prevHorseIdentityKeyRef.current === null) {
      prevHorseIdentityKeyRef.current = horseIdentityKey;
      return;
    }
    if (prevHorseIdentityKeyRef.current === horseIdentityKey) return;

    // Horse identity changed for this render index; reset index-local sequence/cache state
    // so dedupe and fallback caches don't carry over from the previous horse.
    prevHorseIdentityKeyRef.current = horseIdentityKey;
    lastProcessedSeqIdRef.current = -1;
    activeSequenceIdRef.current = -1;
    pathPlaybackSeqRef.current = null;
    prevFinishedOrDroppedRef.current = false;

    // Baseline must match what UI will show next. Using board center here causes first live move
    // to briefly originate from center if the horse was already rendered at the start line.
    const baselineXY: [number, number] =
      shouldUseField && hasUsableField && hasBoardCoordinate && fieldForDisplay
        ? [transformRelX(fieldForDisplay.x), transformRelY(fieldForDisplay.y)]
        : centerSpreadXY;
    lastPositionRef.current = baselineXY;
    lastRenderedXYRef.current = baselineXY;
    stableHorseFieldCache.delete(cacheKey);
  }, [
    cacheKey,
    horseIdentityKey,
    sequenceId,
    isOwnHorse,
    horsePlayerId,
    fieldForDisplay,
    fieldForDisplay?.x,
    fieldForDisplay?.y,
    shouldUseField,
    hasUsableField,
    hasBoardCoordinate,
    centerSpreadXY,
  ]);

  /**
   * Sequence handling must run before `syncRenderedXYLayoutEffect` so `lastRenderedXYRef` still
   * reflects the previous frame (e.g. teleport field) when the first live path starts.
   */
  useLayoutEffect(() => {
    if (sequenceId <= lastProcessedSeqIdRef.current) {
      return;
    }
    lastProcessedSeqIdRef.current = sequenceId;
    activeSequenceIdRef.current = sequenceId;
    pathPlaybackSeqRef.current = null;

    // Baseline from what was actually shown last frame, not raw spring (can be stale vs teleport).
    lastPositionRef.current = lastRenderedXYRef.current;
    animatedPositionApi.stop();

    if (path.length === 0) {
      const resetXY: [number, number] = [transformRelX(cw / 2), transformRelY(ch / 2)];
      lastPositionRef.current = resetXY;
      lastRenderedXYRef.current = resetXY;
      animatedPositionApi.set({ xy: resetXY });
      return;
    }

    const isHistoryMode = mode === 'history' || speedProfile === 'history';

    // If we are starting a new live path after a teleport (start-line placement), make the handoff
    // deterministic: force spring to the path start when the previous rendered baseline equals it.
    // This avoids a single-frame fallback to the spring's stale center-ish value.
    if (mode === 'live' && path.length > 1) {
      const pathStartXY: [number, number] = [transformRelX(path[0].x), transformRelY(path[0].y)];
      const baseline = lastPositionRef.current;
      const dBaseline = Math.hypot(baseline[0] - pathStartXY[0], baseline[1] - pathStartXY[1]);
      if (dBaseline <= PATH_START_XY_EPS) {
        animatedPositionApi.set({ xy: pathStartXY });
        lastPositionRef.current = pathStartXY;
        lastRenderedXYRef.current = pathStartXY;
      }
    }

    // First remote live move: spring may still be near board center while the horse was shown at the
    // start line via teleport/direct transform. Only snap when the spring is closer to the board
    // center than to path start (avoids spurious set() when spring is already near the path end).
    if (mode === 'live' && path.length > 1) {
      const pathStartXY: [number, number] = [transformRelX(path[0].x), transformRelY(path[0].y)];
      const boardCenterXY: [number, number] = [transformRelX(cw / 2), transformRelY(ch / 2)];
      const springXY = xy.get() as [number, number];
      const dSpringStart = Math.hypot(springXY[0] - pathStartXY[0], springXY[1] - pathStartXY[1]);
      const dSpringCenter = Math.hypot(
        springXY[0] - boardCenterXY[0],
        springXY[1] - boardCenterXY[1],
      );
      const dBaseline = Math.hypot(
        lastPositionRef.current[0] - pathStartXY[0],
        lastPositionRef.current[1] - pathStartXY[1],
      );
      if (
        dSpringStart > PATH_START_XY_EPS &&
        dBaseline <= PATH_START_XY_EPS &&
        dSpringCenter < dSpringStart
      ) {
        animatedPositionApi.set({ xy: pathStartXY });
        lastPositionRef.current = pathStartXY;
        lastRenderedXYRef.current = pathStartXY;
      }
    }

    // Live: if path[0] does not match rendered position, replace first point so the first
    // segment starts from the actual spring position (reducer stores path end in x,y).
    let pathForAnimation = path;
    if (mode === 'live' && path.length > 1) {
      const lastPos = lastPositionRef.current;
      const firstXY: [number, number] = [transformRelX(path[0].x), transformRelY(path[0].y)];
      const d0 = Math.hypot(firstXY[0] - lastPos[0], firstXY[1] - lastPos[1]);
      if (d0 > PATH_START_XY_EPS) {
        pathForAnimation = [
          { x: boardXFromRelX(lastPos[0]), y: boardYFromRelY(lastPos[1]) },
          ...path.slice(1),
        ];
      }
    }

    const targetXY: [number, number] = [
      transformRelX(pathForAnimation[pathForAnimation.length - 1].x),
      transformRelY(pathForAnimation[pathForAnimation.length - 1].y),
    ];
    const steps =
      pathForAnimation.length > 1
        ? pathForAnimation.slice(1).map((p) => ({ x: p.x, y: p.y, length: 1 }))
        : [];

    if (mode === 'teleport') {
      if (isHistoryMode) {
        const fromXY = lastPositionRef.current;
        lastPositionRef.current = targetXY;
        lastRenderedXYRef.current = targetXY;
        const duration = getStepDurationMs(speedProfile, true, 1);
        animatedPositionApi.start({
          from: { xy: fromXY },
          to: { xy: targetXY },
          config: { duration, easing: easeQuadOut },
        });
      } else {
        lastPositionRef.current = targetXY;
        lastRenderedXYRef.current = targetXY;
        animatedPositionApi.set({ xy: targetXY });
      }
      return;
    }

    if (steps.length === 0) {
      if (isHistoryMode) {
        const fromXY = lastPositionRef.current;
        lastPositionRef.current = targetXY;
        lastRenderedXYRef.current = targetXY;
        const duration = getStepDurationMs(speedProfile, true, 1);
        animatedPositionApi.start({
          from: { xy: fromXY },
          to: { xy: targetXY },
          config: { duration, easing: easeQuadOut },
        });
      } else {
        lastPositionRef.current = targetXY;
        lastRenderedXYRef.current = targetXY;
        animatedPositionApi.set({ xy: targetXY });
      }
      return;
    }

    pathPlaybackSeqRef.current = sequenceId;
    const fromXY = isHistoryMode ? lastPositionRef.current : undefined;
    animatedPositionApi.start({
      ...(fromXY !== undefined && { from: { xy: fromXY } }),
      to: async (next) => {
        try {
          for (let i = 0; i < steps.length; i++) {
            if (activeSequenceIdRef.current !== sequenceId) {
              // Interrupted by a newer sequence: sync tracking ref to current spring position.
              const cur = xy.get() as [number, number];
              lastPositionRef.current = cur;
              lastRenderedXYRef.current = cur;
              pathPlaybackSeqRef.current = null;
              return;
            }
            const step = steps[i];
            const stepXY: [number, number] = [transformRelX(step.x), transformRelY(step.y)];
            const isLastStep = i === steps.length - 1;
            const duration = getStepDurationMs(speedProfile, isLastStep, step.length);
            await next({
              to: { xy: stepXY },
              config: { duration, easing: easeQuadOut },
            });
          }
          if (activeSequenceIdRef.current === sequenceId) {
            lastPositionRef.current = targetXY;
            lastRenderedXYRef.current = targetXY;
          }
        } finally {
          if (pathPlaybackSeqRef.current === sequenceId) {
            pathPlaybackSeqRef.current = null;
          }
        }
      },
    });
  }, [sequenceId, path, mode, speedProfile, animatedPositionApi, xy]);

  /** After sequence handling, keep `lastRenderedXYRef` aligned with teleport vs spring for the next handoff. */
  useLayoutEffect(() => {
    const teleportVisual =
      mode === 'teleport' &&
      speedProfile !== 'history' &&
      shouldUseField &&
      hasBoardCoordinate &&
      hasUsableField &&
      fieldForDisplay;

    if (teleportVisual) {
      lastRenderedXYRef.current = [
        transformRelX(fieldForDisplay!.x),
        transformRelY(fieldForDisplay!.y),
      ];
      return;
    }

    const springXY = xy.get() as [number, number];
    lastRenderedXYRef.current = springXY;
  }, [
    mode,
    speedProfile,
    shouldUseField,
    hasBoardCoordinate,
    hasUsableField,
    fieldForDisplay,
    fieldForDisplay?.x,
    fieldForDisplay?.y,
    fieldForDisplay?.animationIntent?.path,
    path.length,
    xy,
  ]);

  useEffect(() => {
    if (!(isGameRunning && shouldUseField && hasBoardCoordinate && hasUsableField)) return;
    // Do not snap spring to Redux destination while multi-step path animation runs for this sequence.
    if (pathPlaybackSeqRef.current !== null && pathPlaybackSeqRef.current === sequenceId) {
      return;
    }
    const renderedXY = xy.get() as [number, number];
    const targetXY: [number, number] = [
      transformRelX(fieldForDisplay!.x),
      transformRelY(fieldForDisplay!.y),
    ];
    const deltaX = renderedXY[0] - targetXY[0];
    const deltaY = renderedXY[1] - targetXY[1];
    const outOfSync = Math.abs(deltaX) > RECONCILE_XY_EPS || Math.abs(deltaY) > RECONCILE_XY_EPS;
    if (!outOfSync) return;

    lastPositionRef.current = targetXY;
    lastRenderedXYRef.current = targetXY;
    animatedPositionApi.set({ xy: targetXY });
  }, [
    index,
    isOwnHorse,
    isGameRunning,
    shouldUseField,
    hasBoardCoordinate,
    hasUsableField,
    fieldForDisplay?.x,
    fieldForDisplay?.y,
    sequenceId,
    xy,
    animatedPositionApi,
    fieldForDisplay,
  ]);

  useEffect(() => {
    const nowFinishedOrDropped = horseFinished || isDropped;
    if (!nowFinishedOrDropped) {
      prevFinishedOrDroppedRef.current = false;
      return;
    }
    if (nowFinishedOrDropped && !prevFinishedOrDroppedRef.current) {
      lastPositionRef.current = centerSpreadXY;
      lastRenderedXYRef.current = centerSpreadXY;
      animatedPositionApi.start({
        to: { xy: centerSpreadXY },
        config: { duration: 300, easing: easeQuadOut },
      });
      prevFinishedOrDroppedRef.current = true;
    }
  }, [
    index,
    isOwnHorse,
    horseFinished,
    isDropped,
    isGameRunning,
    shouldUseField,
    fieldForDisplay?.x,
    fieldForDisplay?.y,
    sequenceId,
    animatedPositionApi,
    centerSpreadXY,
  ]);

  const transform = useCallback(
    (px: number, py: number) =>
      `translate3d(calc(${px} * 100vw - (${w} / 2)), calc(${py} * (100vw / ${vw} * ${vh}) - (${h} / 2)), 0)`,
    [w, h],
  );
  const directTeleportTransform = useMemo(() => {
    if (mode !== 'teleport') return null;
    if (speedProfile === 'history') return null;
    if (!(shouldUseField && hasBoardCoordinate && hasUsableField)) return null;
    return transform(transformRelX(fieldForDisplay!.x), transformRelY(fieldForDisplay!.y));
  }, [
    mode,
    speedProfile,
    shouldUseField,
    hasBoardCoordinate,
    hasUsableField,
    transform,
    fieldForDisplay,
  ]);

  const [{ rotation }, animatedRotationApi] = useSpring<{ rotation: number; config: SpringConfig }>(
    () => ({
      from: { rotation: 0 },
      config: { tension: 280, friction: 160 },
    }),
    [],
  );
  useEffect(() => {
    isDropped && animatedRotationApi.start({ to: { rotation: rotation.get() > 0 ? 0 : 1440 } });
  }, [isDropped, rotation, animatedRotationApi]);

  return (
    <animated.div
      data-testid={`horse-${index}`}
      data-horse-index={index}
      className={cn(
        'overflow-none absolute origin-center cursor-default bg-cover bg-center bg-no-repeat',
        isHighlightedHorse && 'z-10 overflow-visible',
      )}
      style={{
        width: w,
        height: h,
        transform: directTeleportTransform ?? xy.to(transform),
      }}
    >
      <div
        className={cn(
          'relative h-full w-full origin-center',
          isOwnHighlightedHorse && 'animate-horseOwnPulse',
        )}
      >
        {/* Icon first: HorseIcon paints a full opaque circle; highlight must stack above or it is invisible. */}
        <div className="relative z-0 h-full w-full origin-center">
          <HorseIcon
            className="h-full w-full drop-shadow-xl"
            style={{ animationDelay: `${rank * 200}ms` }}
            color={horseColor}
            orientation={transformRelY(y) < 0.5 ? 'l' : 'r'}
            rotation={rotation}
            onOpenToolTip={() => analytics?.logEvent('show_horse_info', { isOwnHorse })}
          >
            <HorseInfo
              {...{ playerName, horseName, isDropped }}
              country={horsePlayer.country}
              horseTotalRank={currentRound > 0 ? totalRank : undefined}
              handicap={Turfmaster.handi[horseHandi]}
              jockeyName={horse?.jockeyName}
              userId={horsePlayer?.userId}
              {...(horseColor !== horseRealColor && {
                horseRealColor: HorseColorValues[horseRealColor],
              })}
            />
          </HorseIcon>
        </div>
        {isHighlightedHorse && (
          <div
            className="pointer-events-none absolute inset-0 z-1"
            style={{ transform: 'translateZ(0)' }}
            aria-hidden
          >
            {/* box-shadow ring sits outside the icon box: inner edge of the stroke flush with horse disc (no gap vs border-inset). */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                boxShadow: `0 0 0 max(1px, ${
                  isOwnHighlightedHorse ? ANCHOR_RING_WIDTH_VW_OWN : ANCHOR_RING_WIDTH_VW_DEFAULT
                }) ${activeRingStroke}`,
              }}
            />
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center will-change-transform',
                isOwnHighlightedHorse ? 'animate-horseOrbitOwn' : 'animate-horseOrbitOther',
              )}
            >
              <svg className="h-full w-full overflow-visible" viewBox="0 0 100 100" aria-hidden>
                <circle
                  cx="50"
                  cy="50"
                  fill="none"
                  r={ORBIT_RADIUS_VIEWBOX}
                  strokeDasharray={`${ORBIT_DASH_LENGTH} ${ORBIT_DASH_GAP}`}
                  strokeLinecap="round"
                  strokeWidth={ORBIT_STROKE_WIDTH_VIEWBOX}
                  style={{ stroke: activeOrbitStroke }}
                  transform="rotate(-90 50 50)"
                />
                <circle
                  cx="50"
                  cy="50"
                  fill="none"
                  r={ORBIT_RADIUS_VIEWBOX}
                  strokeDasharray={`${ORBIT_DASH_LENGTH} ${ORBIT_DASH_GAP}`}
                  strokeLinecap="round"
                  strokeWidth={ORBIT_STROKE_WIDTH_VIEWBOX}
                  style={{ stroke: activeOrbitStroke }}
                  transform="rotate(90 50 50)"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </animated.div>
  );
};

export default memo(Horse);
